import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonTable from '../../../../common/table/ComonTable';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../../services/apiService';
import SearchBar from '../../../../common/formfields/SearchBar';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';

const AmenitiesTypeList = () => {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [amenities, setAmenities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("None");
    const [pageInfo, setPageInfo] = useState({
        pageNumber: 1,
        pageSize: 10,
    });

    const fetchAmenities = async () => {
        setLoading(true);
        try {
            const response = await apiServices({
                method: "GET",
                url: `/ResidentialAmenity/GetAllResidentialAmenityTypes`
            });

            if (response?.data.IsSuccess) {
                let data = response?.data?.data2;
                // Sort by amenityTypeId descending as Angular did
                data.sort((a, b) => b.amenityTypeId - a.amenityTypeId);
                setAmenities(data);
            }
        } catch (error) {
            console.error("Error fetching amenities:", error);
            toast.error("Failed to fetch amenities types.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAmenities();
    }, []);

    const filteredAmenities = useMemo(() => {
        let data = [...amenities];

        if (searchQuery.trim()) {
            const searchLower = searchQuery.toLowerCase();
            data = data.filter(item =>
                item.amenityTypeName?.toLowerCase().includes(searchLower)
            );
        }

        if (statusFilter !== 'None') {
            const isActive = statusFilter === 'Active' ? 1 : 0;
            data = data.filter(item => item.isActiveId === isActive);
        }

        return data;
    }, [amenities, searchQuery, statusFilter]);

    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * pageInfo.pageSize;
        return filteredAmenities.slice(start, start + pageInfo.pageSize);
    }, [filteredAmenities, currentPage, pageInfo.pageSize]);

    const totalRecords = filteredAmenities.length;
    const totalPages = Math.ceil(totalRecords / pageInfo.pageSize) || 1;

    const handleLimitChange = (newLimit) => {
        setPageInfo(prev => ({ ...prev, pageSize: newLimit }));
        setCurrentPage(1);
    };

    const handleEdit = (row) => {
        navigate(`/zentres/Master-management/AmenitiesType/form/${row.amenityTypeId}`, { state: { amenityData: row } });
    };

    const tableHeaders = [
        { label: "NO", key: "no" },
        { label: "AMENITY TYPE NAME", key: "amenityTypeName" },
        { label: "IMAGE", key: "image" },
        { label: "STATUS", key: "isActiveId" },
        { label: "ACTIONS", key: "actions" }
    ];

    const renderCell = (key, row, index) => {
        switch (key) {
            case "no":
                return <span className="text-xs font-semibold text-[#1E293B]">{index + 1 + (currentPage - 1) * pageInfo.pageSize}</span>;
            case "amenityTypeName":
                return <span className="text-xs font-semibold text-[#1E293B]">{row.amenityTypeName || "—"}</span>;
            case "image":
                return (
                    <div className="flex justify-center items-center">
                        {row.image ? (
                            <img src={row.image} alt="Amenity" className="h-8 w-8 rounded object-cover" />
                        ) : (
                            <span className="text-xs text-gray-400">-</span>
                        )}
                    </div>
                );
            case "isActiveId":
                const isActive = row.isActiveId === 1;
                const statusColor = isActive ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#FEF9C3] text-[#D97706]";
                return (
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black shadow-sm uppercase ${statusColor}`}>
                        {isActive ? 'Active' : 'Inactive'}
                    </span>
                );
            default:
                return "—";
        }
    };

    const headerAction = (
        <div className="flex flex-col md:flex-row items-center gap-3">
            <SearchBar
                onSearch={setSearchQuery}
                placeholder="Search amenities type..."
                className="w-full md:w-64"
            />
            <select
                value={statusFilter}
                onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                }}
                className="w-full md:w-auto px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-indigo-500 focus:border-indigo-500 text-gray-700 bg-white"
            >
                <option value="None">All Status</option>
                <option value="Active">Active</option>
                <option value="InActive">Inactive</option>
            </select>
        </div>
    );

    return (
        <div className="bg-white px-6 py-4 border flex flex-col rounded-lg flex-1 overflow-y-auto gap-6 pt-4 scrollbar-hide">
            <div className='flex flex-col gap-1 shrink-0'>
                <Breadcrumbs />
                <PageHeadingSection
                    Navbar={true}
                    mainHeading="Amenity Type List"
                    subHeading="Manage your residential Amenity Types"
                    primaryBtnText="Add New"
                    primaryBtnIcon={faPlus}
                    PrimaryOnClick={() => navigate('/zentres/Master-management/AmenitiesType/form')}
                />
            </div>

            <div className="shrink-0 flex-1 flex flex-col">
                <CommonTable
                    tableHeading="Amenity Types"
                    data={paginatedData}
                    headers={tableHeaders}
                    renderCell={(key, row) => renderCell(key, row, paginatedData.indexOf(row))}
                    headerAction={headerAction}
                    pagination={{
                        pageSize: pageInfo.pageSize,
                        pageNumber: currentPage,
                        totalPages: totalPages,
                        totalRecords: totalRecords
                    }}
                    loading={loading}
                    onPageChange={setCurrentPage}
                    onLimitChange={handleLimitChange}
                    onEdit={handleEdit}
                    showActionCol={true}
                />
            </div>
        </div>
    );
};

export default AmenitiesTypeList;
